import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, withCtx as _withCtx, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "grid grid-cols-2 gap-x-8" }
const _hoisted_2 = { class: "col-span-2" }
const _hoisted_3 = { class: "relative min-h-300" }
const _hoisted_4 = {
  key: 0,
  class: "absolute top-0 bottom-0 left-0 right-0 flex items-center justify-center"
}
const _hoisted_5 = /*#__PURE__*/_createElementVNode("p", { class: "text-xl text-center" }, [
  /*#__PURE__*/_createTextVNode(" No phone numbers found. "),
  /*#__PURE__*/_createElementVNode("br"),
  /*#__PURE__*/_createTextVNode("Please, update search request ")
], -1)
const _hoisted_6 = [
  _hoisted_5
]
const _hoisted_7 = {
  key: 2,
  class: "grid gap-y-2 max-h-80 overflow-auto"
}
const _hoisted_8 = ["onClick"]
const _hoisted_9 = { class: "text-center mt-2" }
const _hoisted_10 = {
  key: 0,
  class: "text-xs"
}
const _hoisted_11 = { class: "flex justify-center w-full" }

import { reactive, ref, watch } from 'vue';
import { required } from '@vuelidate/validators';
import { useToast } from 'vue-toastification';
import debounce from 'lodash/debounce';
import {
	useToggle,
	UiCardDivider,
	UiInput,
	UiLoader,
	UiForm,
	UiButton,
	useLoading,
	useForm
} from '@/ui-kit';
import { Nullable, pickBy } from '@/utils';
import { useNewPhoneNumbers } from '../composition';
import { PhoneNumberQueryItem } from '../types';
import { CancelTokenSource, Cancel } from 'axios';


export default /*#__PURE__*/_defineComponent({
  __name: 'AddPhoneNumberDialog',
  setup(__props, { expose }) {

const toast = useToast();
const { loading: numbersLoading, toggleLoading } =
	useLoading(true);
const { isOpened, toggleOpened } = useToggle();
const { searchPhoneNumbers, addPhoneNumber } =
	useNewPhoneNumbers();
const {
	onSubmit,
	fillFormData,
	resetForm,
	formData,
	loading
} = useForm(
	{
		friendlyName: '',
		phoneNumber: ''
	},
	{
		friendlyName: {
			required
		},
		phoneNumber: {
			required
		}
	}
);

const search = reactive({
	inLocality: '',
	areaCode: '',
	contains: ''
});
const phoneNumbers = ref<PhoneNumberQueryItem[]>([]);
const selectedNumber =
	ref<Nullable<PhoneNumberQueryItem>>(null);
const cancel = ref<Nullable<CancelTokenSource>>(null);

const openDialog = async () => {
	selectedNumber.value = null;
	resetForm({ friendlyName: '', phoneNumber: '' });
	loadNumbers();
	toggleOpened(true);
};

const closeDialog = () => {
	toggleOpened(false);
	Object.assign(search, {
		inLocality: '',
		areaCode: '',
		contains: ''
	});
	cancelRequest();
};

const selectFormNumber = (number: PhoneNumberQueryItem) => {
	fillFormData(number);
	selectedNumber.value = number;
};

const submit = onSubmit(async (data) => {
	await addPhoneNumber(data);
	closeDialog();
	toast.success(
		`${data.friendlyName} was successfully added.`
	);
});

const cancelRequest = () => {
	if (cancel.value) {
		cancel.value.cancel('cancelled');
	}
};

const loadNumbers = async (payload?: typeof search) => {
	const data = pickBy(payload || {}, (value) => !!value);

	toggleLoading(true);
	cancelRequest();

	const { request, cancelSource } =
		searchPhoneNumbers(data);

	cancel.value = cancelSource;

	try {
		phoneNumbers.value = await request();
	} catch (e: any) {
		if (e?.message === 'cancelled') {
			return;
		}
		console.log(
			'SEARCH ERROR ->',
			e.response.data.message
		);
		toast.error(
			`Error happened while searching phone numbers ${
				e.response?.data?.message || ''
			}`,
			{
				timeout: false
			}
		);
		phoneNumbers.value = [];
	}

	toggleLoading(false);
	cancel.value = null;
};

watch(
	search,
	debounce((data) => {
		if (!isOpened.value) {
			return;
		}
		if (data.areaCode && data.areaCode.length !== 3) {
			console.log(data.areaCode);
			return;
		}
		loadNumbers(data);
	}, 500)
);

expose({
	openDialog
});

return (_ctx: any,_cache: any) => {
  const _component_el_dialog = _resolveComponent("el-dialog")!

  return (_openBlock(), _createBlock(_component_el_dialog, {
    customClass: "is-max-500",
    modelValue: _unref(isOpened),
    onClosed: closeDialog,
    closeOnClickModal: !selectedNumber.value,
    closeOnPressEscape: !selectedNumber.value,
    showClose: !selectedNumber.value,
    top: "4vh"
  }, {
    default: _withCtx(() => [
      (!selectedNumber.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createVNode(_unref(UiCardDivider), { title: "Search phone number" }),
            _createElementVNode("div", _hoisted_1, [
              _createVNode(_unref(UiInput), {
                name: "city",
                placeholder: "Search by city name",
                modelValue: search.inLocality,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((search.inLocality) = $event)),
                novalidate: ""
              }, null, 8, ["modelValue"]),
              _createVNode(_unref(UiInput), {
                name: "areaCode",
                placeholder: "Search by area code",
                modelValue: search.areaCode,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((search.areaCode) = $event)),
                type: "number",
                novalidate: "",
                disabled: _unref(loading)
              }, null, 8, ["modelValue", "disabled"]),
              _createElementVNode("div", _hoisted_2, [
                _createVNode(_unref(UiInput), {
                  name: "digits",
                  modelValue: search.contains,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((search.contains) = $event)),
                  placeholder: "Search by digits or phrases",
                  novalidate: "",
                  disabled: _unref(loading)
                }, null, 8, ["modelValue", "disabled"])
              ])
            ]),
            _createVNode(_unref(UiCardDivider), { class: "-mt-4" }),
            _createElementVNode("div", _hoisted_3, [
              (
						!_unref(numbersLoading) &&
						!phoneNumbers.value.length
					)
                ? (_openBlock(), _createElementBlock("div", _hoisted_4, _hoisted_6))
                : _createCommentVNode("", true),
              (_unref(numbersLoading))
                ? (_openBlock(), _createBlock(_unref(UiLoader), {
                    key: 1,
                    wrapperClass: "base"
                  }))
                : (_openBlock(), _createElementBlock("div", _hoisted_7, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(phoneNumbers.value, (number, i) => {
                      return (_openBlock(), _createElementBlock("div", {
                        class: "flex justify-between text-sm p-2 cursor-pointer border border-solid border-dark-blue hover:bg-gray-100 transition",
                        key: i,
                        onClick: ($event: any) => (selectFormNumber(number))
                      }, [
                        _createElementVNode("span", null, _toDisplayString(number.friendlyName), 1),
                        _createElementVNode("span", null, _toDisplayString(number.locality) + ", " + _toDisplayString(number.region), 1)
                      ], 8, _hoisted_8))
                    }), 128))
                  ]))
            ]),
            _createVNode(_unref(UiCardDivider)),
            _createElementVNode("div", _hoisted_9, [
              (
						!_unref(numbersLoading) &&
						phoneNumbers.value.length
					)
                ? (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(phoneNumbers.value.length) + " results", 1))
                : _createCommentVNode("", true)
            ])
          ], 64))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createVNode(_unref(UiCardDivider), {
              title: `Confirm selection of ${selectedNumber.value.friendlyName}`
            }, null, 8, ["title"]),
            _createVNode(_unref(UiForm), { onSubmit: _unref(submit) }, {
              buttons: _withCtx(() => [
                _createElementVNode("div", _hoisted_11, [
                  _createVNode(_unref(UiButton), {
                    class: "mr-4",
                    type: "button",
                    theme: "primary-outlined",
                    disabled: _unref(loading),
                    onClick: _cache[5] || (_cache[5] = ($event: any) => (selectedNumber.value = null))
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode("Cancel")
                    ]),
                    _: 1
                  }, 8, ["disabled"]),
                  _createVNode(_unref(UiButton), { loading: _unref(loading) }, {
                    default: _withCtx(() => [
                      _createTextVNode("Confirm phone number")
                    ]),
                    _: 1
                  }, 8, ["loading"])
                ])
              ]),
              default: _withCtx(() => [
                _createVNode(_unref(UiInput), {
                  name: "phoneNumber",
                  label: "Phone number",
                  novalidate: "",
                  disabled: true,
                  modelValue: _unref(formData).phoneNumber,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(formData).phoneNumber) = $event))
                }, null, 8, ["modelValue"]),
                _createVNode(_unref(UiInput), {
                  disabled: _unref(loading),
                  name: "friendlyName",
                  label: "Friendly name",
                  modelValue: _unref(formData).friendlyName,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((_unref(formData).friendlyName) = $event))
                }, null, 8, ["disabled", "modelValue"])
              ]),
              _: 1
            }, 8, ["onSubmit"])
          ], 64))
    ]),
    _: 1
  }, 8, ["modelValue", "closeOnClickModal", "closeOnPressEscape", "showClose"]))
}
}

})